import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { Group } from '../../../../models/group.model';
import { Prayer } from '../../../../models/prayer.model';
import { MemberService } from '../../../../services/member.service';
import * as prayerActions from '../../../../state/actions/prayer.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as prayerSelector from '../../../../state/selectors/prayer.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';

@Component({
  selector: 'app-edit-prayer',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    BtnBackComponent,
    AsyncPipe,
    ReactiveFormsModule,
    RouterLink,
    LoadingDotsComponent,
  ],
  templateUrl: './edit-prayer.component.html',
  styleUrls: ['./edit-prayer.component.css'],
})
export class EditPrayerComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  toastr: ToastrService = inject(ToastrService);

  pageForm: FormGroup;

  today: Date = new Date();
  moment = moment;

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  group$: Observable<Group | null | undefined>;

  prayerId: string | null = this.route.snapshot.paramMap.get('prayerId');
  prayer: Prayer | null = null;
  prayerSub: Subscription | undefined;

  working: boolean = false;
  isSearching: boolean = false;

  private searchText: string | undefined;
  searchTerm: string | undefined;
  members: any = [];

  constructor(
    private store: Store<AppState>,
    private memberService: MemberService,
  ) {
    this.pageForm = new FormGroup({
      subject: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(500),
      ]),
      category: new FormControl('', Validators.required),
      requestedBy: new FormControl(''),
      requestedById: new FormControl(''),
      expiry: new FormControl('', Validators.required),
    });

    this.group$ = this.store.pipe(
      select(groupSelector.getGroupById(this.groupId!)),
    );
  }

  search(event: any) {
    this.searchText = event.target.value;
  }

  searchMembers() {
    if (!this.searchText) {
      return;
    }
    this.isSearching = true;
    this.memberService
      .searchMembers(this.searchText, this.groupId!)
      .then((data: any) => {
        if (data.length === 0) {
          this.pageForm.get('requestedBy')?.setErrors({ notFound: true });
          this.members = [];
        } else {
          this.members = data;
        }
        this.isSearching = false;
      })
      .catch((error: any) => {
        console.error('Error searching members', error);
        this.isSearching = false;
        this.members = [];
        this.pageForm.get('requestedBy')?.setErrors({ searchFailed: true });
      });
  }

  ngOnInit(): void {
    this.prayerSub = this.store
      .select(prayerSelector.getPrayerById(this.prayerId!))
      .subscribe((prayer) => {
        if (prayer) {
          this.prayer = prayer;
          this.pageForm.patchValue(
            {
              subject: prayer.subject,
              description: prayer.description,
              category: prayer.category,
              requestedBy: prayer.requestedBy,
              requestedById: prayer.requestedById,
              expiry: moment(prayer.expiry.toDate()).format('YYYY-MM-DD'),
            },
            { emitEvent: false },
          );
        }
      });
  }

  ngOnDestroy() {
    this.prayerSub?.unsubscribe();
  }

  trackById(index: number, item: any): void {
    return item.id;
  }

  setMember(member: any) {
    this.pageForm.get('requestedBy')?.setValue(member.name);
    this.pageForm.get('requestedById')?.setValue(member.memberId);
    this.members = [];
    // console.log(`RequestedBy: ${member.name} (${member.memberId})`);
  }

  setCategory(item: string) {
    this.pageForm.get('category')?.setValue(item);
  }

  update() {
    if (this.pageForm.invalid) {
      return;
    }

    if (this.prayer == null) {
      this.toastr.error('Prayer not found', 'Error');
      return;
    }

    const temp: Prayer = {
      ...this.pageForm.value,
      expiry: moment(this.pageForm.get('expiry')?.value)
        .startOf('day')
        .toDate(),
      groupId: this.groupId!,
      status: this.prayer?.status,
      id: this.prayer.id,
    };

    this.store.dispatch(prayerActions.update({ prayer: temp }));
  }
}
